import { useState } from "react";
import { Popover } from "@snpjs/core";

import { TableFilterProps } from "./TableFilter.types";
import styles from "./TableFilter.module.scss";

type TimeRange = [string, string];

const toInputValue = (value: string): string => {
  if (/^\d{6}$/.test(value)) {
    return `${value.slice(0, 2)}:${value.slice(2, 4)}:${value.slice(4, 6)}`;
  }
  if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
  return "";
};

export const TimeFilterComponent: React.FC<TableFilterProps> = ({ value, onChange }) => {
  const [start, end] = (value as TimeRange | undefined) ?? ["", ""];
  const [activeRange, setActiveRange] = useState<0 | 1>(0);
  const normalizedRange: TimeRange = [toInputValue(start), toInputValue(end)];
  const activeTime = normalizedRange[activeRange] || "00:00:00";
  const [hours, minutes, seconds] = activeTime.split(":");

  const updateTimePart = (part: 0 | 1 | 2, nextValue: string) => {
    const currentParts = (normalizedRange[activeRange] || "00:00:00").split(":");
    currentParts[part] = nextValue;
    const nextRange: TimeRange = [...normalizedRange];
    nextRange[activeRange] = currentParts.join(":");
    onChange(nextRange);
  };

  const options = (count: number) => Array.from({ length: count }, (_, index) =>
    String(index).padStart(2, "0")
  );

  const content = (
    <div className={styles.timestampPopover}>
      <div className={styles.timeRangeTabs}>
        <button
          className={activeRange === 0 ? styles.activeTimeRange : styles.timeRangeButton}
          type="button"
          onClick={() => setActiveRange(0)}
        >
          From: {normalizedRange[0] || "HH:MM:SS"}
        </button>
        <button
          className={activeRange === 1 ? styles.activeTimeRange : styles.timeRangeButton}
          type="button"
          onClick={() => setActiveRange(1)}
        >
          To: {normalizedRange[1] || "HH:MM:SS"}
        </button>
      </div>
      <div className={styles.timeSelectors}>
        <label className={styles.timePart}>
          Hour
          <select className={styles.control} value={hours} onChange={(event) => updateTimePart(0, event.target.value)}>
            {options(24).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <label className={styles.timePart}>
          Minute
          <select className={styles.control} value={minutes} onChange={(event) => updateTimePart(1, event.target.value)}>
            {options(60).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <label className={styles.timePart}>
          Second
          <select className={styles.control} value={seconds} onChange={(event) => updateTimePart(2, event.target.value)}>
            {options(60).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
      </div>
      {(start || end) && (
        <div className={styles.dateActions}>
          <button className={styles.clearButton} type="button" onClick={() => onChange(undefined)}>
            Clear
          </button>
        </div>
      )}
    </div>
  );

  const displayValue = (time: string) => time ? toInputValue(time) : "HH:MM:SS";

  return (
    <Popover content={content} placement="bottom-start" usePortal={true}>
      <div className={styles.timestampRangeTarget}>
        <span className={styles.timestampTargetValue}>{displayValue(start)}</span>
        <span className={styles.dateRangeSeparator}>to</span>
        <span className={styles.timestampTargetValue}>{displayValue(end)}</span>
        {(start || end) && (
          <button
            className={styles.clearButton}
            type="button"
            aria-label="Clear time range"
            onClick={(event) => {
              event.stopPropagation();
              onChange(undefined);
            }}
          >
            ×
          </button>
        )}
      </div>
    </Popover>
  );
};